import {
  Bookmark,
  CalendarDays,
  Layers,
  SlidersHorizontal,
  Video,
  Users,
  Bell,
  Library,
  BookOpen,
  PenLine,
  Radio,
  LayoutDashboard,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useSession } from '@/hooks/useSession';
import { useAsyncRead } from '@/hooks/useAsyncRead';
import type { Capabilities, Family } from './contracts';
import { networks } from './service';
import { ShortcutGroup, ShortcutLink } from './ShortcutLink';

const icons: Record<string, LucideIcon> = {
  '/mixes': SlidersHorizontal,
  '/starter-packs': Layers,
  '/saved-searches': Bookmark,
  '/collections': Library,
  '/guides': BookOpen,
  '/events': CalendarDays,
  '/creators-hub/dashboard': LayoutDashboard,
  '/collaborations': PenLine,
  '/channels': Radio,
  '/playlists': Video,
  '/notification-delivery': Bell,
};

const destinations: Record<string, { family: Family; to: string; label: string }[]> = {
  home: [{ family: 'mixes_packs', to: '/mixes', label: 'Your Signal Mixes' }],
  explore: [
    { family: 'mixes_packs', to: '/starter-packs', label: 'Starter Packs' },
    { family: 'saved_search_notification_budget', to: '/saved-searches', label: 'Saved searches' },
  ],
  bookmarks: [
    { family: 'collections_guides', to: '/collections', label: 'Shared collections' },
    { family: 'collections_guides', to: '/guides', label: 'Guides' },
  ],
  communities: [{ family: 'events_live', to: '/events', label: 'Events & AMAs' }],
  creator: [
    { family: 'creator_collaboration', to: '/creators-hub/dashboard', label: 'Creator workspace' },
    { family: 'creator_collaboration', to: '/collaborations', label: 'Collaborative drafts' },
    { family: 'creator_collaboration', to: '/channels', label: 'Creator channels' },
  ],
  videos: [{ family: 'external_media', to: '/playlists', label: 'External video playlists' }],
  live: [{ family: 'events_live', to: '/events', label: 'Scheduled sessions & replays' }],
  notifications: [
    {
      family: 'saved_search_notification_budget',
      to: '/notification-delivery',
      label: 'Push budget & quiet hours',
    },
  ],
};
/** Absent/disabled capabilities never advertise unavailable functionality. */
export function NetworkLinks({
  destination,
  onCollaborate,
}: {
  destination: keyof typeof destinations;
  onCollaborate?: () => void;
}) {
  const { currentUser } = useSession();
  const result = useAsyncRead<Capabilities | null>(
    networks.capabilities,
    currentUser?.id ?? 'guest',
    null,
  );
  const links = destinations[destination]?.filter((d) => result.data?.flags?.[d.family]) ?? [];
  if (!links.length) return null;
  return (
    <ShortcutGroup aria-label={`${destination} shortcuts`}>
      {links.map((d) => {
        const Icon = icons[d.to] ?? Users;
        return (
          <ShortcutLink
            key={d.to}
            to={d.to}
            onClick={
              d.to === '/collaborations' && onCollaborate
                ? (e) => {
                    e.preventDefault();
                    onCollaborate();
                  }
                : undefined
            }
          >
            <Icon size={16} aria-hidden="true" />
            {d.label}
          </ShortcutLink>
        );
      })}
    </ShortcutGroup>
  );
}
